import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Heart, Clock, CheckCircle, Flower2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SpaMaskGuide() {
  const navigate = useNavigate();

  const maskTypes = [
    { type: "Sheet Masks", best: "Quick hydration boost", time: "15-20 min", tip: "Don't let it dry on skin - remove while still moist" },
    { type: "Clay Masks", best: "Deep pore cleansing", time: "10-15 min", tip: "Only use on oily areas, not dry patches" },
    { type: "Cream Masks", best: "Dry/mature skin", time: "15-20 min", tip: "Can sleep in overnight versions" },
    { type: "Gel Masks", best: "Soothing, cooling", time: "15-20 min", tip: "Great after sun exposure" },
    { type: "Exfoliating Masks", best: "Brightening, texture", time: "5-10 min", tip: "Don't use more than 1x/week" }
  ];

  const diyRecipes = [
    { name: "Honey Glow Mask", ingredients: ["2 tbsp raw honey", "1 tbsp plain yogurt", "1 tsp turmeric"], benefit: "Hydrating, brightening, anti-inflammatory" },
    { name: "Oatmeal Calm Mask", ingredients: ["2 tbsp ground oatmeal", "1 tbsp honey", "1 tbsp warm water"], benefit: "Soothing for sensitive/irritated skin" },
    { name: "Avocado Nourish Mask", ingredients: ["1/2 ripe avocado", "1 tbsp honey", "1 tsp olive oil"], benefit: "Deep moisture for dry skin" },
    { name: "Green Tea Refresh Mask", ingredients: ["1 tbsp green tea powder", "1 tbsp honey", "1 tbsp yogurt"], benefit: "Antioxidant boost, reduces redness" }
  ];

  const spaSetup = [
    "Light a scented candle",
    "Play relaxing music or nature sounds",
    "Prepare cucumber slices for eyes",
    "Have a warm cup of herbal tea ready",
    "Put your phone on silent",
    "Wear a comfortable robe",
    "Keep a soft towel nearby"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-semibold mb-4">
            Week 3 • Day 5 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Spa Mask Treatment
          </h1>
          <p className="text-xl text-secondary-600">Your At-Home Facial Experience</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-rose-400 to-pink-400 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Sparkles className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Self-Care Ritual</h3>
              <p className="text-rose-100 text-lg">
                This isn't just skincare—it's a moment of peace. Apply your mask, lie down with cucumber slices on your eyes, 
                and let yourself fully relax for 15 minutes. You deserve this.
              </p>
            </div>
          </div>
        </section>

        {/* Spa Setup */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Flower2 className="w-6 h-6 text-rose-500" />
            Create Your Spa Atmosphere
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {spaSetup.map((item, index) => (
              <div key={index} className="flex items-center gap-2 p-3 bg-rose-50 rounded-lg text-secondary-700">
                <CheckCircle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                {item}
              </div>
            ))}
          </div>
        </section>

        {/* Mask Types */}
        <section className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-8 border-2 border-rose-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Choose Your Mask</h2>
          <div className="space-y-3">
            {maskTypes.map((mask, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <h3 className="font-bold text-rose-700 md:w-1/4">{mask.type}</h3>
                  <span className="text-sm text-secondary-600 md:w-1/4">Best for: {mask.best}</span>
                  <span className="text-xs bg-rose-100 text-rose-600 px-2 py-1 rounded-full w-fit">{mask.time}</span>
                </div>
                <p className="text-sm text-rose-600 italic mt-2">{mask.tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* DIY Recipes */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Heart className="w-6 h-6 text-rose-500" />
            DIY Mask Recipes
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {diyRecipes.map((recipe, index) => (
              <div key={index} className="p-4 bg-rose-50 rounded-xl">
                <h3 className="font-bold text-rose-700 mb-2">{recipe.name}</h3>
                <ul className="space-y-1 mb-3">
                  {recipe.ingredients.map((ing, i) => (
                    <li key={i} className="text-sm text-secondary-700 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-rose-400 rounded-full"></span>
                      {ing}
                    </li>
                  ))}
                </ul>
                <p className="text-xs bg-white rounded-lg px-2 py-1 text-secondary-600">{recipe.benefit}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-secondary-500 mt-4 italic">
            Mix ingredients, apply to clean skin, leave for 15 minutes, then rinse with lukewarm water.
          </p>
        </section>
      </main>
    </div>
  );
}

export default SpaMaskGuide;
